import { useCallback, useMemo, useState } from "react";
import Fuse from "fuse.js";

const INITIAL_OPTION_BATCH = 10;
const MIN_FUSE_LENGTH = 2;
const FUSE_THRESHOLD = 0.3;

const FUSE_KEYS = [
  "target_name",
  "target_name_en",
  "building_name",
  "building_name_en",
  "area_name",
  "area_name_en",
  "floor_name",
  "floor_name_en",
  "description",
  "description_en",
  "properties_text",
  "properties_text_en",
];

// map search に関するロジックをまとめたカスタムフック
function useMapSearch(listArray) {
  const [searchValue, setSearchValue] = useState(null);
  const [visibleOptionCount, setVisibleOptionCount] =
    useState(INITIAL_OPTION_BATCH);

  const fuse = useMemo(() => {
    if (!listArray || listArray.length === 0) return null;

    return new Fuse(listArray, {
      keys: FUSE_KEYS,
      threshold: FUSE_THRESHOLD, // 0=完全一致、1=最も緩い
      includeMatches: true, // マッチ情報を含める
      ignoreLocation: true, //文字列のどこにヒットしたかを一切考慮しない
      minMatchCharLength: MIN_FUSE_LENGTH, // 最小マッチ文字数
    });
  }, [listArray]);

  // 検索クエリが変更されたときに表示オプション数をリセット
  const resetVisibleOptionCount = useCallback(() => {
    setVisibleOptionCount(INITIAL_OPTION_BATCH);
  }, []);

  // 検索クエリと表示オプション数をリセット
  const resetSearchState = useCallback(() => {
    setSearchValue(null);
    setVisibleOptionCount(INITIAL_OPTION_BATCH);
  }, []);

  // オプションリストのスクロールイベントハンドラー
  const handleListboxScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    if (!listboxNode) return;

    const reachedBottom =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 1;

    if (reachedBottom) {
      setVisibleOptionCount((prev) => prev + INITIAL_OPTION_BATCH);
    }
  }, []);

  // オプションのフィルタリング関数
  const filterOptions = useCallback(
    (options, { inputValue }) => {
      if (inputValue.length < MIN_FUSE_LENGTH) {
        return options.slice(0, visibleOptionCount);
      }
      if (!fuse) {
        return options.slice(0, visibleOptionCount);
      }
      const results = fuse.search(inputValue);
      return results.slice(0, visibleOptionCount);
    },
    [fuse, visibleOptionCount],
  );

  return {
    searchValue,
    setSearchValue,
    resetVisibleOptionCount,
    resetSearchState,
    handleListboxScroll,
    filterOptions,
  };
}

export default useMapSearch;
